import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, later, schedule, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { ImageCropData, Service } from 'webapp-types';
import { BoardCustomContainer, DataPerformanceSummaryType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';
import { centerElements } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import type { BoardBaseArgs } from 'webapp/components/board/base';
import BoardBaseComponent from 'webapp/components/board/base';
import BoardBlocksWallComponent from 'webapp/components/board/blocks/wall';
import BoardManualSyncButtonComponent from 'webapp/components/board/manual-sync-button';
import BoardMasterDateRangeSwitchComponent from 'webapp/components/board/master-date-range-switch';
import BoardMasterFilterComponent from 'webapp/components/board/master-filter';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataPerformanceSummaryButtonComponent from 'webapp/components/data-performance/summary-button';
import DataStatusTextComponent from 'webapp/components/data-status-text';
import FetchLoaderComponent from 'webapp/components/fetch-loader';
import FileLogoWithUploadComponent from 'webapp/components/file/logo-with-upload';
import FormInputComponent from 'webapp/components/form/input';

import Board from 'webapp/models-custom/board';
import type DatawallGrid from 'webapp/models-custom/datawall-grid';
import type DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';
import type Source from 'webapp/models-custom/source';

import type { DataPerformanceSummaryObject } from 'webapp/services/model-manager/data-performance-summaries';

interface BoardWallArgs extends BoardBaseArgs {
	showDesignerLink?: boolean;
	isPortraitView?: boolean; // if true, switch x with y and width with height. only when isViewOnly is true
	isInIframe?: boolean;
	isLeftSidebarOpen?: boolean;
	isRightSidebarOpen?: boolean;
	onDatawallGridChange?: (datawallGrid: DatawallGrid | null) => void;
	changeMdr?: (dateRange: DateRange, compare: DsCompare | null) => void;
	boardSources?: Source[];
}

type ResizeFn = (e: { target?: EventTarget | null }) => void;

export default class BoardWallComponent extends BoardBaseComponent<BoardWallArgs> {
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;

	_resizeFn: ResizeFn;

	constructor(owner: Owner, args: BoardWallArgs) {
		super(owner, args);

		this.updatePositioning(true);
		this.scheduleUpdatePositioning(); // opening report editor with first slide board that already has data loaded

		const resizeFn: ResizeFn = (e) => {
			const target = e.target;
			if (target && !$(target).hasClass('ui-resizable')) {
				// ignore events bubbled from resizable blocks
				debounce(this.updatePositioning, false, this.debounceUpdatePositioningTimeout);
			}
		};
		this._resizeFn = resizeFn;

		$(window).on('resize.datawallcenter', this._resizeFn);
		$('#slider').on('resize.datawallcenter', this._resizeFn);
	}

	// slide resize animation takes 200ms
	get debounceUpdatePositioningTimeout() {
		if (this.isInReportPageless || this.isInReportSlide) {
			return 300;
		}
		return 100;
	}

	get dataPerformanceSummaryObject(): DataPerformanceSummaryObject | null {
		const showDataPerformanceSummary =
			!this.args.isInIframe &&
			!this.navigationService.isLoadedFromSnapshooter &&
			this.navigationService.isDatawallApp &&
			this.currentUserService.isLoggedIn &&
			this.mmDataPerformanceSummariesService.showInUIAndEnabled &&
			!!this.accountService.id &&
			this.accountService.id === this.navigationService.spaceIdOfDatawall;

		// TODO add conditions for agency user logged in in agency + space board, agency user in space + agency board,  agency user in space1 + space2 board

		if (showDataPerformanceSummary) {
			const objectId = this.model.id || '';

			const isPreview = this.isViewOnly && this.args.boardState.board.isPreview;

			return {
				objectType: isPreview
					? DataPerformanceSummaryType.BOARD_PREVIEW
					: DataPerformanceSummaryType.BOARD,
				objectId,
			};
		}

		return null;
	}

	get currentZoom(): number {
		return this.fontSize * 10;
	}

	get fontSize(): number {
		return Board.getFontsizeFromWidth(this.longerSideSize);
	}

	get showLock(): boolean {
		return this.navigationService.isDatawallAndShowLock;
	}

	get showManualSyncButton(): boolean {
		if (this.navigationService.isDatawallApp) {
			return this.navigationService.isDatawallAndHasAccessToBoard;
		} else {
			return true;
		}
	}

	// ---- Sizing stuff ------

	@tracked longerSideSize = 1920;

	get shorterSideSize(): number {
		// height on normal, width on portrait
		return (this.longerSideSize / 16) * 9; // 16:9 ratio
	}

	get isInReportSlide() {
		return this.args.inCustomContainer === BoardCustomContainer.REPORT_SLIDE;
	}

	get isInReportPageless() {
		return this.args.inCustomContainer === BoardCustomContainer.REPORT_PAGELESS;
	}

	get doCenterPreview(): boolean {
		return (
			this.isViewOnly &&
			!this.isInReportSlide &&
			!this.isInReportPageless &&
			!this.isInMetricInsight
		);
	}

	@action
	scheduleUpdatePositioning(): void {
		debounce(this.updatePositioning, false, 100);
	}

	@action
	updatePositioning(isInitialCheck = false): void {
		if (!isAlive(this)) {
			return;
		}

		this.updateOuterWidth(isInitialCheck);

		if (this.doCenterPreview) {
			scheduleOnce('afterRender', this, this.positionReadOnlyPreview);
		}
	}

	@action
	override endBoardNameEdit(): void {
		super.endBoardNameEdit();
		this.analyticsService.event(TrackingEvent.DATABOARD_RENAME, { origin: 'designer' });
	}

	updateOuterWidth(isInitialCheck: boolean): void {
		let longerSide;

		if (this.args.isInDesigner) {
			const ct = $('#designer-board-ct--wall');
			longerSide = ct.width()!;
		} else {
			let ct;
			if (this.isInReportSlide) {
				ct = $('.report_slide_inner');
			} else if (this.isInReportPageless) {
				ct = $('.pageless-wrapper');
			} else {
				ct = $('#slider');

				if (!ct.length) {
					ct = $('#datawall');
				}
			}

			if (!ct.length) {
				return;
			}

			let shorterSide;
			if (this.isInReportPageless) {
				// include width for pageless reports to achieve edge to edge
				longerSide = ct[0]!.offsetWidth;
				shorterSide = ct[0]!.offsetHeight;
			} else {
				longerSide = (this.args.isPortraitView ? ct.height() : ct.width())!;
				shorterSide = (this.args.isPortraitView ? ct.width() : ct.height())!;
			}

			if (shorterSide * 16 < longerSide * 9 && !this.isInReportPageless) {
				longerSide = (shorterSide * 16) / 9;
			}
		}

		// when called from constructor, skip checking against the current value (same runloop issue)
		if (isAlive(this) && (isInitialCheck || longerSide !== this.longerSideSize)) {
			this.longerSideSize = longerSide;
		}
	}

	positionReadOnlyPreview(): void {
		later(() => {
			const ct = this.el,
				el = ct?.find('.view-board-inner');
			if (isAlive(this) && ct?.length && el?.length) {
				centerElements(el, {
					centerOn: ct,
				});

				$('#slider-nav').css({
					fontSize: this.fontSize + 'px',
					paddingBottom: Math.max(ct.height()! - el.height()! - el.position().top, 0),
				});
				el.trigger('custom.afterAnimate');
			}
		}, 400);
	}

	// ---- Styles stuff ------

	get bgStyle() {
		let style = '';
		if (this.model.bg) {
			style = `background-image:url("${this.model.bg}");`;
		}
		style += `opacity:${this.model.bgOpacity};`;
		return htmlSafe(style);
	}

	get viewInnerStyle() {
		const styles = {
			width: this.args.isPortraitView ? this.shorterSideSize : this.longerSideSize,
			height: this.args.isPortraitView ? this.longerSideSize : this.shorterSideSize,
			'font-size': this.fontSize,
		};
		const string = Object.keys(styles)
			.map((prop) => {
				const p = prop as keyof typeof styles;
				return `${p}:${styles[p]}px`;
			})
			.join(';');
		return htmlSafe(string);
	}

	// ---- Actions ----

	@action
	changeLogo(url: string, originalUrl: string, crop: ImageCropData | null): void {
		this.analytics.event(TrackingEvent.DATABOARD_VISUAL_SETTINGS_MODIFIED, {
			logoAdded: !!url,
		});

		const board = this.model,
			isNew = board.isNew;

		board.logo = url;
		board.logoOriginal = originalUrl;
		board.logoCrop = crop;
		board
			.save()
			.then((boardRecord) => {
				if (isNew) {
					// new board, mark it so we don't request 'data' again
					void this.mmBoardsService.markBoardRequestedAndSubscribeToDataChannel(
						boardRecord.id!,
					);

					schedule('afterRender', this, () => {
						// redirect to this board so URL changes
						const newRoute = `/layout-designer/${boardRecord.id}`;
						this.navigationService.replaceRoute(newRoute);
					});
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	datawallGridChange(datawallGrid?: DatawallGrid | null): void {
		if (this.args.onDatawallGridChange) {
			this.args.onDatawallGridChange(datawallGrid || null);
		}
	}

	@action
	openEnableCustomDataUpdatePopup() {
		this.mmBoardsService.openEnableCustomDataUpdatePopup(this.model, this.args.trackingOrigin);
	}

	@action
	teardownView(): void {
		$(window).off('resize.datawallcenter', this._resizeFn);
		$('#slider').off('resize.datawallcenter', this._resizeFn);
	}

	@action
	changeMdr(dateRange: DateRange | null, compare: DsCompare | null, applyToWholeLoop?: boolean) {
		this.masterDateRangeChanged(dateRange, compare, applyToWholeLoop);

		if (this.args.changeMdr && applyToWholeLoop) {
			this.args.changeMdr(dateRange!, compare);
		}
	}
	<template>
		<div
			class="view-board--wall bgTheme-{{this.model.bgTheme}}
				colorTheme-{{this.model.themeId}}
				{{unless this.showBottomInfo 'no-bottom'}}"
			style={{this.gradientStyle}}
			data-id={{this.model.id}}
			{{didInsert this.setupView}}
			{{didUpdate this.checkIfBoardLoaded this.model.id this.model.isDataLoading}}
			{{didUpdate (fn this.updatePositioning false) this.model.id}}
			{{didUpdate
				this.scheduleUpdatePositioning
				@isPortraitView
				@isLeftSidebarOpen
				@isRightSidebarOpen
			}}
			{{willDestroy this.teardownView}}
		>
			<div class="bg" style={{this.bgStyle}}></div>

			<div
				class="view-board-inner {{if this.showBoardFetchLoader 'faded'}}"
				style={{this.viewInnerStyle}}
			>

				<div class="view-board-content">
					<div class="view-board-content-inner content-inner" data-id={{this.model.id}}>

						{{#if this.showLoader}}
							<div class="bar-container">
								<BarLoaderComponent @size="big" />
							</div>
						{{else}}
							<BoardBlocksWallComponent
								@boardState={{@boardState}}
								@isViewOnly={{this.isViewOnly}}
								@currentZoom={{this.currentZoom}}
								@skipBlockFetchLoaders={{this.showBoardFetchLoader}}
								@disableBlockSizeAnimations={{@isInIframe}}
								@isPortraitView={{@isPortraitView}}
								@skipActions={{@skipActions}}
								@onDatawallGridChange={{this.datawallGridChange}}
								@trackingOrigin={{@trackingOrigin}}
							/>
						{{/if}}

						<div class="bottomInfo {{if @isInDesigner 'canEdit'}}">
							{{#if this.showBottomInfo}}
								<div class="bottomInfo-left">
									<FileLogoWithUploadComponent
										@image={{this.model.shownLogo}}
										@originalImage={{this.model.logoOriginal}}
										@imageCrop={{this.model.logoCrop}}
										@canEdit={{@isInDesigner}}
										@onChange={{this.changeLogo}}
									/>

									{{#if this.canEditName}}
										{{#if this.isEditingName}}
											<h1 class="editing">
												<FormInputComponent
													name="name"
													maxlength="65"
													@autofocus={{true}}
													@value={{this.editedName}}
													@focusOut={{this.endBoardNameEdit}}
													@loseFocusOnEnter={{true}}
												/>
											</h1>
										{{else}}
											<h1 class="canEdit">
												<label
													{{on "click" this.startBoardNameEdit}}
													class="editableTitle"
												>
													{{this.model.name}}
													<span class="edit-icon">
														<CommonUiIconComponent
															@name="Pencil"
															@size="dynamic"
														/>
													</span>
												</label>
											</h1>
										{{/if}}
									{{else}}
										<h1>
											{{this.model.name}}
										</h1>
										{{#if @showDesignerLink}}
											<a
												href="/layout-designer/{{this.model.boardIdForPublicUrl}}"
												class="designer-link"
												target="_blank"
											>
												<CommonUiIconComponent
													@name="PencilRuler"
													@size="dynamic"
												/>
												<span>Edit</span>
											</a>
										{{/if}}
									{{/if}}
									{{#if this.dataPerformanceSummaryObject}}
										<div class="summary-wrapper">
											<DataPerformanceSummaryButtonComponent
												@object={{this.dataPerformanceSummaryObject}}
												@trackingOrigin="preview_databoard"
											/>
										</div>
									{{/if}}
								</div>
							{{/if}}

							<div class="bottomInfo-right">
								{{#if this.showBottomInfo}}
									{{#if this.showLock}}
										<span class="lock-icon">
											<CommonUiIconComponent
												@name="DatablockLibrary"
												@size="dynamic"
											/>
										</span>
									{{/if}}

									{{#if this.showFilterMasterSwitcher}}
										<BoardMasterFilterComponent
											@boardState={{@boardState}}
											@save={{this.changeMasterFilter}}
											@theme={{this.theme}}
											@isDisabled={{this.accountService.isDemoAccount}}
											@iconSize="dynamic"
										/>
									{{/if}}

									{{#if this.showDateRangeMasterSwitcher}}
										<BoardMasterDateRangeSwitchComponent
											@boardState={{@boardState}}
											@onChange={{this.changeMdr}}
											@theme={{this.theme}}
											@isDisabled={{this.accountService.isDemoAccount}}
											@openAllBlockPreviewProps={{true}}
											@slideCount={{@slideCount}}
										/>
									{{/if}}

									{{#if this.showResetPreviewToOriginal}}
										<a
											class="default-view"
											{{on "click" this.resetPreviewToOriginal}}
										>
											<span>Reset</span>
										</a>
									{{/if}}
								{{/if}}

								{{#if this.isSampleData}}
									<div class="warning">
										<div class="sample">Note: Showing sample data</div>
									</div>
								{{else}}
									{{#if this.isExpiredSpace}}
										<div class="warning">
											<div class="expired">
												Account has expired. Data refresh is on hold.
												{{#if this.showHelpArticles}}
													<a
														href="https://help.databox.com/data-sync-storage-security-in-databox"
														target="_blank"
													>
														Refresh my data
													</a>
												{{/if}}
											</div>
										</div>
									{{else if this.isInactiveSpace}}
										<div class="warning">
											<div class="expired">
												Data refresh is on hold due to account inactivity.
												{{#if this.showHelpArticles}}
													<a
														href="https://help.databox.com/data-sync-storage-security-in-databox"
														target="_blank"
													>
														Refresh my data
													</a>
												{{/if}}
											</div>
										</div>
									{{else}}
										{{#if this.showLoadingInsteadOfLastSync}}
											<span class="sync loading">
												Loading...
											</span>
										{{else if this.model.lastSyncText}}
											<DataStatusTextComponent
												@text={{this.lastSyncText}}
												@context="board"
												@lastSync={{this.model.lastSync}}
												@skipActions={{@skipActions}}
												@openEnableCustomDataUpdatePopup={{this.openEnableCustomDataUpdatePopup}}
												@boardSources={{@boardSources}}
											/>
											{{#if this.showManualSyncButton}}
												<BoardManualSyncButtonComponent
													@model={{this.model}}
													@trackingOrigin={{@trackingOrigin}}
													@iconSize="dynamic"
												/>
											{{/if}}
										{{/if}}
									{{/if}}
								{{/if}}

								{{#unless this.showLoader}}
									{{#unless this.hideBranding}}
										{{#if @skipActions}}
											<a><span class="powered">Powered by</span></a>
										{{else}}
											<a href="https://databox.com" target="_blank">
												<span class="powered">Powered by</span>
											</a>
										{{/if}}
									{{/unless}}
								{{/unless}}
							</div>
						</div>

					</div>

					{{#if this.showBoardFetchLoader}}
						<FetchLoaderComponent
							@colors={{this.model.themeMainColorPair}}
							@type="board"
						/>
					{{/if}}
				</div>
			</div>

		</div>
	</template>
}
